import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, AlertTriangle, MessageCircle, MapPin, Phone, Globe, Mail, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import api from "../lib/api";
import { kitById, kitWhatsAppText, FOLLOWUP_SECTION } from "../lib/eduKits";
import { CLINIC, LogoTile, doctorDisplay, doctorPhotoUrl, brandColor, buildWhatsAppBrandedMessage } from "../lib/clinic";
import { openWhatsAppRaw } from "../lib/whatsapp";

// Generic printable patient-education handout — fully clinic-branded so a
// printed or photographed copy always carries the clinic identity.
export default function EduKitPage() {
  const { kid } = useParams();
  const [kit, setKit] = useState(null);
  const [lang, setLang] = useState("mr");
  const [err, setErr] = useState("");
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/edukits/${kid}`);
        setKit(r.data.kit);
        setLang(r.data.kit.language || "mr");
      } catch (e) {
        setErr("Kit not found");
      }
    })();
  }, [kid]);

  if (err) return <div className="p-10 text-center text-slate-500">{err}</div>;
  if (!kit) return <div className="p-10 text-center text-slate-400">Loading…</div>;
  const def = kitById(kit.kit_type);
  if (!def) return <div className="p-10 text-center text-slate-500">Unknown kit type</div>;
  const L = (b) => b[lang] || b.en;
  const brand = brandColor();
  const phones = (CLINIC.phones || []).join(" · ");

  const shareWhatsApp = async () => {
    if (!kit.patient_id) { toast.error("No patient linked to this kit"); return; }
    setSharing(true);
    try {
      const pr = await api.get(`/patients/${kit.patient_id}`);
      const mobile = pr.data?.patient?.mobile;
      if (!mobile) { toast.error("No mobile number on file for this patient"); return; }
      let link = "";
      try {
        const pl = await api.post("/portal/link", { patient_id: kit.patient_id, expires_hours: 24 * 30, reuse: true });
        link = pl.data?.portal_url || "";
      } catch (e) { console.debug("[silent]", e); }
      const waLang = lang === "mr" ? "mr" : "en";
      const body = kitWhatsAppText(def, waLang) +
        (link ? `\n\n🔗 ${waLang === "mr" ? "तुमची संपूर्ण काळजी किट ऑनलाइन उघडा" : "Open your full care kit online"}: ${link}${waLang !== "en" ? `?lang=${waLang}` : ""}` : "");
      const msg = buildWhatsAppBrandedMessage({
        lang: waLang,
        title: def.title[waLang] || def.title.en,
        body,
        patientName: kit.patient_name || "",
        kind: "education",
        dateStr: new Date(kit.issued_at).toLocaleDateString("en-IN"),
      });
      openWhatsAppRaw(mobile, msg);
      toast.success(waLang === "mr" ? "WhatsApp उघडत आहे…" : "Opening WhatsApp…");
    } catch (e) {
      console.debug("[silent]", e);
      toast.error("Could not share on WhatsApp");
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      <div className="max-w-3xl mx-auto p-4 print:p-0">
        <div className="flex items-center justify-between mb-3 print:hidden">
          <div className="text-[10px] rounded-md border border-slate-300 overflow-hidden flex">
            <button onClick={() => setLang("en")} className={`px-3 py-1 ${lang === "en" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`} data-testid="edukit-page-lang-en">EN</button>
            <button onClick={() => setLang("mr")} className={`px-3 py-1 ${lang === "mr" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`} data-testid="edukit-page-lang-mr">मराठी</button>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={shareWhatsApp} disabled={sharing} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="edukit-whatsapp-btn">
              <MessageCircle size={15} className="mr-1.5" /> {sharing ? "Sending…" : "WhatsApp Kit"}
            </Button>
            <Button onClick={() => window.print()} className="bg-slate-900 hover:bg-slate-700 text-white" data-testid="edukit-print-btn">
              <Printer size={15} className="mr-1.5" /> Print
            </Button>
          </div>
        </div>

        <div
          className="bg-white rounded-xl print:rounded-none shadow print:shadow-none overflow-hidden"
          style={{ WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}
          data-testid="edukit-handout"
        >
          {/* ── Clinic-branded header ─────────────────────────────────── */}
          <header className="px-6 pt-5 print:px-4 print:pt-4" data-testid="edukit-branded-header">
            <div className="flex items-start justify-between gap-4 pb-3 border-b-2" style={{ borderColor: brand }}>
              <div className="flex items-start gap-3 min-w-0">
                <LogoTile className="h-14 w-14 shrink-0" padding="p-1.5" />
                <div className="min-w-0">
                  <div className="text-lg font-extrabold leading-tight" style={{ color: brand }}>{CLINIC.name}</div>
                  {CLINIC.specialties && <div className="text-[10px] font-semibold text-slate-600 uppercase tracking-wide">{CLINIC.specialties}</div>}
                  {CLINIC.address && <div className="text-[9.5px] text-slate-500 mt-1 leading-snug flex items-start gap-1"><MapPin size={10} className="mt-[1px] shrink-0" /><span>{CLINIC.address}</span></div>}
                  <div className="text-[9.5px] text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                    {phones && <span className="flex items-center gap-1"><Phone size={10} /> {phones}</span>}
                    {CLINIC.timings && <span className="flex items-center gap-1"><Clock size={10} /> {CLINIC.timings}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2.5 shrink-0">
                <div className="text-right">
                  <div className="text-sm font-bold text-slate-900">{doctorDisplay(kit.issued_by || CLINIC.doctorName)}</div>
                  {CLINIC.qualifications && <div className="text-[8.5px] text-slate-500 max-w-[200px] leading-snug">{CLINIC.qualifications}</div>}
                  {CLINIC.regNo && <div className="text-[8.5px] text-slate-500">{CLINIC.regNo}</div>}
                </div>
                <img
                  src={doctorPhotoUrl()}
                  alt={CLINIC.doctorName}
                  className="h-14 w-14 rounded-full object-cover border-2 shrink-0"
                  style={{ borderColor: brand }}
                  data-testid="edukit-doctor-photo"
                />
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-xl font-bold text-slate-900">{L(def.title)}</div>
              <div className="text-[9.5px] px-2.5 py-1 rounded-full text-white font-semibold uppercase tracking-wider" style={{ backgroundColor: brand }}>
                {lang === "mr" ? "रुग्ण शिक्षण किट" : "Patient Education Kit"}
              </div>
            </div>
            <div className="text-[11px] text-slate-500 mt-1 mb-4 flex flex-wrap gap-x-4">
              {kit.patient_name && <span><strong>{kit.patient_name}</strong>{kit.patient_age ? ` · ${kit.patient_age} yrs` : ""}{kit.patient_sex ? ` · ${kit.patient_sex}` : ""}</span>}
              <span>{new Date(kit.issued_at).toLocaleDateString("en-IN")}</span>
              {kit.issued_by && <span>{lang === "mr" ? "डॉक्टर" : "Doctor"}: {kit.issued_by}</span>}
              {(kit.detected_medicines || []).length > 0 && (
                <span>{lang === "mr" ? "औषध" : "Medicine"}: {kit.detected_medicines.map((m) => m.brand_name || m.generic_name).filter(Boolean).join(", ")}</span>
              )}
            </div>
          </header>

          <div className="px-6 print:px-4">
            {def.sections.map((s) => (
              <section key={s.id} className={`mb-4 print-avoid-break rounded-lg border p-3.5 ${s.tone === "danger" ? "border-red-300 bg-red-50/50" : "border-slate-200 bg-slate-50/50"}`} data-testid={`edukit-section-${s.id}`}>
                <div className={`text-sm font-bold mb-2 ${s.tone === "danger" ? "text-red-800" : "text-slate-800"}`}>{L(s.title)}</div>
                <ul className="space-y-1.5">
                  {s.items.map((it, i) => (
                    <li key={i} className="text-[12.5px] leading-snug text-slate-700 flex gap-2">
                      <span className={s.tone === "danger" ? "text-red-600" : "text-emerald-600"}>{s.tone === "danger" ? "✗" : "✓"}</span>
                      <span>{L(it)}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ))}

            <section className="mb-4 print-avoid-break rounded-lg border-2 border-red-400 bg-red-50 p-3.5" data-testid="edukit-danger">
              <div className="text-sm font-bold text-red-800 mb-2 flex items-center gap-1.5">
                <AlertTriangle size={15} /> {L(def.danger.title)}
              </div>
              <ul className="space-y-1.5">
                {def.danger.items.map((it, i) => (
                  <li key={i} className="text-[12.5px] leading-snug text-red-900 flex gap-2"><span>⚠</span><span>{L(it)}</span></li>
                ))}
              </ul>
            </section>

            <section className="mb-4 print-avoid-break rounded-lg border border-indigo-200 bg-indigo-50/50 p-3.5" data-testid="edukit-followup">
              <div className="text-sm font-bold text-indigo-900 mb-2">{L(FOLLOWUP_SECTION.title)}</div>
              <ul className="space-y-1.5">
                {FOLLOWUP_SECTION.items.map((it, i) => (
                  <li key={i} className="text-[12.5px] leading-snug text-slate-700 flex gap-2"><span className="text-indigo-600">→</span><span>{L(it)}</span></li>
                ))}
              </ul>
            </section>
          </div>

          {/* ── Clinic-branded footer strip ───────────────────────────── */}
          <footer
            className="px-6 py-3.5 print:px-4 text-white print-avoid-break"
            style={{ backgroundColor: brand, WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}
            data-testid="edukit-branded-footer"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="text-[12px] font-extrabold">{CLINIC.name}</div>
                {CLINIC.address && <div className="text-[9.5px] opacity-90 leading-snug">{CLINIC.address}</div>}
                <div className="text-[9.5px] opacity-90 flex flex-wrap items-center gap-x-3 mt-0.5">
                  {phones && <span className="flex items-center gap-1"><Phone size={9} /> {phones}</span>}
                  {CLINIC.website && <span className="flex items-center gap-1"><Globe size={9} /> {CLINIC.website}</span>}
                  {CLINIC.email && <span className="flex items-center gap-1"><Mail size={9} /> {CLINIC.email}</span>}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[10px] font-semibold">{doctorDisplay(CLINIC.doctorName)}</div>
                {CLINIC.timings && <div className="text-[9px] opacity-90">{CLINIC.timings}</div>}
              </div>
            </div>
            <div className="text-[8px] opacity-75 mt-1.5 pt-1.5 border-t border-white/25">
              {lang === "mr"
                ? "हे पत्रक सामान्य मार्गदर्शनासाठी आहे — तुमच्या डॉक्टरांच्या सल्ल्याची जागा घेत नाही."
                : "This handout is general guidance — it does not replace your doctor's advice."}
              {" · "}{kit.kit_id}
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}
